import {
  Lead,
  GuideSection,
  P,
  UL,
  LI,
  Term,
  GuideLink,
  Callout,
  GuideTable,
} from './guide-ui';

/** Body for "LPQL vs SPL" comparison guide (migration intent from Splunk). */
export function LpqlVsSplGuideBody() {
  return (
    <>
      <Lead>
        If you know Splunk&apos;s SPL, LPQL will feel immediately familiar. That is
        by design. Both use the same pipe-based model: start with a search, then pipe
        results through commands like <Term>stats</Term>, <Term>eval</Term> and{' '}
        <Term>sort</Term>. The differences are under the hood: what engine runs the
        query, how broad the command set is, and how much the AI writes for you. This
        guide compares them.
      </Lead>

      <GuideSection id="shared-model" title="The shared pipe model">
        <P>
          Both languages read left to right: a search expression narrows the events,
          then each <Term>|</Term> pipe transforms the result. The mental model,
          filter, then shape, then aggregate, then sort/limit, is identical, which is
          what makes moving between them straightforward.
        </P>
        <GuideTable
          head={['Task', 'SPL (Splunk)', 'LPQL (LogPulse)']}
          rows={[
            ['Filter by field', 'search level=error', 'level=error'],
            ['Time range', 'earliest=-1h', 'earliest=-1h'],
            ['Count by field', '| stats count by host', '| stats count by host'],
            ['Compute a field', '| eval dur=duration/1000', '| eval dur=duration/1000'],
            ['Filter post-stats', '| where count > 100', '| where count > 100'],
            ['Top values', '| top 10 host', '| top 10 host'],
            ['Sort descending', '| sort -count', '| sort -count'],
            ['Limit', '| head 20', '| head 20'],
          ]}
        />
        <P>
          LPQL also shares <Term>timechart</Term>, <Term>rare</Term>,{' '}
          <Term>dedup</Term>, <Term>table</Term>, <Term>fields</Term>,{' '}
          <Term>rename</Term> and <Term>rex</Term>, and a similar eval-function set
          (string, math, conditional, time, pattern).
        </P>
      </GuideSection>

      <GuideSection id="differences" title="Where they differ">
        <UL>
          <LI><Term>Engine</Term>: LPQL compiles to optimised ClickHouse SQL (columnar speed, sub-200ms search); SPL runs on Splunk&apos;s proprietary engine.</LI>
          <LI><Term>Command breadth</Term>: SPL is older and vast, with hundreds of commands and deep ecosystem apps. LPQL focuses on a curated set (around two dozen pipe commands) that cover the everyday 80–90% of analysis.</LI>
          <LI><Term>Structured / JSON data</Term>: LPQL has first-class JSON-path navigation (<Term>a.b.c</Term>, <Term>a[k=&quot;v&quot;].value</Term>) that maps naturally onto structured logs.</LI>
          <LI><Term>AI generation</Term>: in LogPulse you can ask in natural language and the AI Investigator writes and runs the LPQL for you, showing the query so you can verify.</LI>
          <LI><Term>Pricing model</Term>: the languages are free; the difference that usually drives migration is the platform economics underneath.</LI>
        </UL>
      </GuideSection>

      <GuideSection id="migration" title="Migrating from SPL">
        <P>
          Because the syntax overlaps so heavily, most everyday SPL searches translate
          to LPQL with little or no change. The filter-pipe-aggregate skeleton is the
          same. The work is mostly in the long tail of SPL-specific commands and
          macros that have no direct equivalent, where you reshape the query to the
          LPQL command set. Natural-language search lowers that cost further: you can
          describe the result and let the AI produce the LPQL.
        </P>
        <Callout title="Familiar on purpose">
          <p>
            LPQL keeps the pipe-based syntax SPL users already know so teams are
            productive on day one, then adds ClickHouse speed and AI generation. This
            is an educational comparison. See the{' '}
            <GuideLink href="/compare">comparison overview</GuideLink> for the
            platform-level picture.
          </p>
        </Callout>
      </GuideSection>

      <GuideSection id="how-logpulse-helps" title="LPQL in LogPulse">
        <P>
          LPQL is the query language across LogPulse: search, detections, KPIs and
          MCP tools all build on it. You can write it directly, or use natural-language{' '}
          <GuideLink href="/ai-log-search">AI log search</GuideLink> to generate it.
          See{' '}
          <GuideLink href="/guides/what-is-log-management">what is log management</GuideLink>{' '}
          for where it fits, and the{' '}
          <GuideLink href="/docs/search/lpql-syntax">LPQL syntax reference</GuideLink>{' '}
          for the full command set.
        </P>
      </GuideSection>
    </>
  );
}
